import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { FormValidatorMessages } from '../enums/forms.enums';
import { CommonChecks } from '../utilities/common-checks.utilities';
import { AddressUtilities } from '../utilities/address.utilities';
import { DateUtilities } from '../utilities/date.utilities';

export class CustomValidators {
  public static email(control: AbstractControl): ValidationErrors | null {
    if (!control.value || !control.value.trim()) {
      return null;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(control.value.trim())) {
      return { email: { message: FormValidatorMessages.EmailInvalid } };
    }
    
    return null;
  }

  public static phone(control: AbstractControl): ValidationErrors | null {
    const { phoneNumber, extension } = control.value || {};

    if (!phoneNumber && extension) {
      return { phone: { message: FormValidatorMessages.PhoneExtensionWithoutNumber } };
    }

    if (phoneNumber && phoneNumber.length > 0 && phoneNumber.length < 10) {
      return { phone: { message: FormValidatorMessages.PhoneInvalid } };
    }

    return null;
  }

  public static slug(control: AbstractControl): ValidationErrors | null {
    if (!control.value || !control.value.trim()) {
      return null;
    }

    const slugValidCharactersRegex = /^[a-z0-9-]+$/;
    if (!slugValidCharactersRegex.test(control.value.trim())) {
      return { slug: { message: FormValidatorMessages.SlugInvalidCharacters } };
    }

    const slugHyphensRegex = /^(?!.*--)[a-z0-9]+(-[a-z0-9]+)*$/;
    if (!slugHyphensRegex.test(control.value.trim())) {
      return { slug: { message: FormValidatorMessages.SlugInvalidHyphens } };
    }
    
    return null;
  }

  // The import takes these columns as free text and the API checks them as digit strings, so a
  // masked phone control would be the wrong shape here: it would rewrite a cell the user did not
  // touch. (DEV-2718)
  public static digitsOnly(control: AbstractControl): ValidationErrors | null {
    const value = (control.value ?? "").toString().trim();
    if (value.length === 0) {
      return null;
    }

    return /^\d+$/.test(value) ? null : { digitsOnly: { message: FormValidatorMessages.DigitsOnly } };
  }

  /**
   * Digits only and within the cap, measured on what is left after `normalize` has run.
   *
   * `ImportRowValidationService.Normalize` in the Core API rewrites both phone columns before it
   * looks at them, and the Import page's field reference tells the reader they may write them that
   * way. Checking the raw text instead would refuse values the API accepts and measure the cap
   * against characters it discards — so the two normalisations below mirror that method exactly,
   * neither more nor less forgiving than the rules the staged row will actually be held to.
   * (DEV-2718)
   */
  public static importPhoneDigits(
    normalize: (value: string) => string,
    maxLength: number,
    message: FormValidatorMessages
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const normalized = normalize((control.value ?? "").toString().trim());

      if (normalized.length === 0) {
        return null;
      }

      const isValid = /^\d+$/.test(normalized) && normalized.length <= maxLength;
      return isValid ? null : { importPhoneDigits: { message } };
    };
  }

  /** The whole leading run of plus signs and spaces, matching the API's `TrimStart('+', ' ')`. */
  public static stripPhoneCountryCodePrefix(value: string): string {
    return value.replace(/^[+ ]+/, '').trim();
  }

  /** Every separator wherever it falls, matching the API's `PhoneSeparators` of space ( ) - and . */
  public static stripPhoneSeparators(value: string): string {
    return value.replace(/[ ().-]/g, '');
  }

  // Blank passes: the API reads an empty Owner or Tenant? as Owner.
  public static oneOfIgnoringCase(allowedValues: string[], message?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value ?? "").toString().trim();
      if (value.length === 0) {
        return null;
      }

      const isAllowed = allowedValues.some(allowed => allowed.toLowerCase() === value.toLowerCase());
      return isAllowed ? null : { oneOf: { message: message ?? FormValidatorMessages.ImportRowOwnerOrTenantInvalid } };
    };
  }

  public static requiredWithMessage(message: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || (typeof control.value === 'string' && !control.value.trim())) {
        return { required: { message } };
      }
      return null;
    };
  }

  public static matchTypeToConfirm(typeToConfirmText: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value?.length > 0 && control.value !== typeToConfirmText) {
        return { matchTypeToConfirm: { message: FormValidatorMessages.TypeToConfirmMismatch } };
      }
      return null;
    };
  }

  public static futureDate(errorMessage: string = 'Date cannot be in the past'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const selectedDate = DateUtilities.parseLocalDate(control.value);
      const today = DateUtilities.startOfToday();

      if (selectedDate < today) {
        return { futureDate: { message: errorMessage } };
      }

      return null;
    };
  }

  public static noFutureDate(errorMessage: string = 'Date cannot be in the future'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const selectedDate = DateUtilities.parseLocalDate(control.value);
      const today = DateUtilities.startOfToday();

      if (selectedDate > today) {
        return { noFutureDate: { message: errorMessage } };
      }

      return null;
    };
  }

  public static dateRangeValidator(
    issuanceDateField: string,
    dueDateField: string,
    endDateField: string,
    frequencyField: string,
    onceValue: any
  ): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const issuanceDateControl = formGroup.get(issuanceDateField);
      const dueDateControl = formGroup.get(dueDateField);
      const endDateControl = formGroup.get(endDateField);
      const frequencyControl = formGroup.get(frequencyField);

      let hasErrors = false;

      if (issuanceDateControl?.value && dueDateControl?.value) {
        const issuanceDate = DateUtilities.parseLocalDate(issuanceDateControl.value);
        const dueDate = DateUtilities.parseLocalDate(dueDateControl.value);

        if (dueDate < issuanceDate) {
          dueDateControl.setErrors({ dueDate: { message: 'Due date cannot be before the issuance date' } });
          hasErrors = true;
        } else {
          if (dueDateControl.hasError('dueDate')) {
            dueDateControl.setErrors(null);
          }
        }
      }

      if (issuanceDateControl?.value && endDateControl?.value &&
          frequencyControl?.value !== onceValue) {
        const issuanceDate = DateUtilities.parseLocalDate(issuanceDateControl.value);
        const endDate = DateUtilities.parseLocalDate(endDateControl.value);

        if (endDate < issuanceDate) {
          CustomValidators.replaceControlErrors(endDateControl, ['endDate', 'endBeforeMonthEnd'], {
            endDate: { message: 'End date cannot be before the issuance date' }
          });
          hasErrors = true;
        } else if (issuanceDate.getDate() > 27) {
          // Send-at-end-of-month applies whenever the issuance day is > 27 (matches the derivation
          // used in the request builder). The API rejects a Schedule End before that month's last
          // day because the schedule would never issue anything.
          const lastDay = DateUtilities.lastDayOfMonth(issuanceDate);
          if (endDate < lastDay) {
            const formatted = DateUtilities.format(lastDay);
            CustomValidators.replaceControlErrors(endDateControl, ['endDate', 'endBeforeMonthEnd'], {
              endBeforeMonthEnd: {
                message: FormValidatorMessages.ChargeScheduleEndDateBeforeMonthEnd.replace('{date}', formatted)
              }
            });
            hasErrors = true;
          } else {
            CustomValidators.replaceControlErrors(endDateControl, ['endDate', 'endBeforeMonthEnd']);
          }
        } else {
          CustomValidators.replaceControlErrors(endDateControl, ['endDate', 'endBeforeMonthEnd']);
        }
      }

      return hasErrors ? { dateValidation: true } : null;
    };
  }

  private static replaceControlErrors(
    control: AbstractControl | null,
    keysOwnedByThisValidator: string[],
    replacementErrors: ValidationErrors = {}
  ): void {
    if (!control) return;
    const remaining = { ...(control.errors || {}) };
    keysOwnedByThisValidator.forEach(key => delete remaining[key]);
    const merged = { ...remaining, ...replacementErrors };
    control.setErrors(Object.keys(merged).length ? merged : null);
  }

  public static validAmount(control: AbstractControl): ValidationErrors | null {
    if (!control.value && control.value !== 0) {
      return null;
    }

    const raw = String(control.value).replace(/,/g, '');
    const parsed = parseFloat(raw);

    if (isNaN(parsed) || !/^\d*\.?\d+$/.test(raw)) {
      return { validAmount: { message: FormValidatorMessages.ChargeScheduleAmountInvalid } };
    }

    return null;
  }

  public static setValidation(control: AbstractControl): ValidationErrors | null {
    // Get form controls
    const lotNumber = control.get('lotNumber');
    const country = control.get('country');
    const primaryAddress = control.get('primaryAddress');
    const secondaryAddress = control.get('secondaryAddress');
    const city = control.get('city');
    const stateProvince = control.get('stateProvince');
    const primaryZip = control.get('primaryZip');

    const hasLotNumber = !CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(lotNumber?.value);
    const hasAddressInfo = AddressUtilities.hasAddressInfo({
        country: country?.value,
        primary: primaryAddress?.value, 
        secondary: secondaryAddress?.value, 
        city: city?.value, 
        stateProvince: stateProvince?.value, 
        primaryZip: primaryZip?.value
      });

    switch (true) {

      case  (hasLotNumber && hasAddressInfo):
        lotNumber?.setValidators([Validators.required]);
        country?.setValidators([Validators.required]);
        primaryAddress?.setValidators([Validators.required]);
        secondaryAddress?.setValidators(null);
        city?.setValidators([Validators.required]);
        stateProvince?.setValidators([Validators.required]);        
        primaryZip?.setValidators([Validators.required]);
        break;

      case (!hasLotNumber && hasAddressInfo):
        lotNumber?.setValidators(null);
        country?.setValidators([Validators.required]);
        primaryAddress?.setValidators([Validators.required]);
        secondaryAddress?.setValidators(null);
        city?.setValidators([Validators.required]);
        stateProvince?.setValidators([Validators.required]);        
        primaryZip?.setValidators([Validators.required]);
        break;
        

      case (hasLotNumber && !hasAddressInfo):
        lotNumber?.setValidators([Validators.required]);
        country?.setValidators(null);
        primaryAddress?.setValidators(null);
        secondaryAddress?.setValidators(null);
        city?.setValidators(null);
        stateProvince?.setValidators(null);
        primaryZip?.setValidators(null);
        break;


      case (!hasLotNumber && !hasAddressInfo):
        lotNumber?.setValidators([Validators.required]);
        country?.setValidators([Validators.required]);
        primaryAddress?.setValidators([Validators.required]);
        secondaryAddress?.setValidators(null);
        city?.setValidators([Validators.required]);
        stateProvince?.setValidators([Validators.required]);                
        primaryZip?.setValidators([Validators.required]);
        break;
    }
    
      country?.updateValueAndValidity({ emitEvent: false });
      stateProvince?.updateValueAndValidity({ emitEvent: false });

    return null;
  }


}
